import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Star } from 'lucide-react';
import { useApp } from '@/store/app';
import { CATEGORY_BY_ID } from '@/registry/categories';
import { cn } from '@/lib/cn';
import { Badge } from './ui/Badge';
import type { Tool } from '@/registry/types';

export function ToolCard({ tool, compact }: { tool: Tool; compact?: boolean }) {
  const navigate = useNavigate();
  const { favorites, toggleFavorite } = useApp();
  const cat = CATEGORY_BY_ID[tool.category];
  const Icon = tool.icon;
  const isFav = favorites.includes(tool.id);

  return (
    <button
      onClick={() => navigate(`/tool/${tool.id}`)}
      className={cn(
        'group relative text-left rounded-2xl border-2 border-fg/85 dark:border-fg/30 bg-surface',
        'shadow-sticker-sm',
        'hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-sticker',
        'active:translate-x-[1px] active:translate-y-[1px] active:shadow-sticker-pressed',
        'transition-all duration-150 overflow-hidden p-4',
      )}
    >
      <div
        className={cn(
          'absolute -top-8 -right-8 size-24 rounded-full opacity-25 blur-2xl group-hover:opacity-50 transition-opacity bg-gradient-to-br',
          cat?.gradient,
        )}
      />
      <div className="relative">
        <div className="flex items-start justify-between mb-2.5">
          <div
            className={cn(
              'size-10 rounded-xl flex items-center justify-center bg-gradient-to-br border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm group-hover:rotate-[-6deg] transition-transform',
              cat?.gradient,
            )}
          >
            <Icon size={18} className="text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.3)]" />
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(tool.id);
              }}
              className={cn(
                'size-6 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition',
                'hover:bg-surface-hover',
                isFav && 'opacity-100',
              )}
            >
              <Star
                size={12}
                className={cn('text-fg-subtle', isFav && 'fill-warning text-warning')}
              />
            </button>
            <ArrowUpRight
              size={12}
              className="text-fg-subtle opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
            />
          </div>
        </div>
        <h3 className="text-sm font-semibold leading-tight">{tool.name}</h3>
        {!compact && (
          <p className="text-xs text-fg-muted mt-1 line-clamp-2">{tool.description}</p>
        )}
        <div className="flex items-center gap-1.5 mt-3">
          <Badge variant="outline">{cat?.name}</Badge>
          {tool.badge && (
            <Badge variant={tool.badge === 'soon' ? 'warning' : 'accent'}>{tool.badge}</Badge>
          )}
          {tool.inBrowser && <Badge variant="success">local</Badge>}
        </div>
      </div>
    </button>
  );
}
